import Service, { service } from '@ember/service'
import { Product, productsForSubscriptionType } from 'district-ui-client/domain/product'
import type { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import type StandardsSet from 'district-ui-client/models/standards-set'
import type { FeatureAccessId } from 'district-ui-client/services/feature-control'
import type FeatureControlService from 'district-ui-client/services/feature-control'
import type FeaturesService from 'district-ui-client/services/features'
import type SessionService from 'district-ui-client/services/session'
import type { IntlService } from 'ember-intl'

const UNSUPPORTED_REX_STANDARDS_SET_NAMES: readonly string[] = [
  'Alabama Course of Study',
  'Arizona Standards',
  'Arkansas Academic Standards',
  'Connecticut Core Standards',
  'Georgia Standards of Excellence',
  'Idaho Content Standards',
  'Illinois Learning Standards',
  'Indiana Academic Standards',
  'Iowa Academic Standards',
  'Kansas Curricular Standards',
  'Kentucky Academic Standards',
  'Louisiana Student Standards',
  'Maryland State Standards',
  'Minnesota Academic Standards',
  'Mississippi College- and Career-Readiness Standards',
  'Missouri Learning Standards',
  'New York State Next Generation Learning Standards',
  'Oklahoma Academic Standards',
  'Oregon Academic Standards',
  'South Carolina College- and Career-Ready Standards',
  'Tennessee Academic Standards',
  'Virginia Standards of Learning',
  'Washington State Learning Standards',
  'West Virginia Standards',
  'Wisconsin Academic Standards',
  'Colorado Academic Standards',
  'Alberta Program of Studies',
  'British Columbia Learning Standards',
  'Quebec Progression of Learning',
  'Newfoundland and Labrador Curriculum',
  'Nova Scotia Curriculum',
  'New Brunswick Curriculum',
  'Prince Edward Island Curriculum',
  'Saskatchewan Curriculum',
  'QLD ACiQ',
  'NT (AC V9.0)',
  'ACT (AC V9.0)',
  'SA (AC V9.0)',
  'Tasmania (AC V9.0)',
  'New Zealand',
  'Ireland PLC / MA',
  'Northern Ireland NIC',
  'Scotland CfE',
  'Wales CfW',
  'Republic of South Africa CAPS',
]

export class ReportRegistryService extends Service {
  @service intl!: IntlService

  @service features!: FeaturesService

  @service featureControl!: FeatureControlService

  @service session!: SessionService

  /**
   * Reports in the registry are enabled by default.
   */
  isReportEnabled(reportPath: ReportPath) {
    const featureFlag = reportsRegistry[reportPath].featureFlag
    const featureAccess = reportsRegistry[reportPath].featureAccess
    const accessGranted = featureAccess === undefined ? true : this.featureControl.canAccess(featureAccess)
    const featureEnabled = featureFlag === undefined ? true : this.features.isEnabled(featureFlag)

    return (
      accessGranted &&
      featureEnabled &&
      !isReportBlockedByStandardsSet(reportPath, this.session.currentDistrict?.standardsSet)
    )
  }

  reportPathsForProduct(product: Product): ReportPath[] {
    return this.allReportPathsForProduct(product).filter((reportPath) => this.isReportEnabled(reportPath))
  }

  private allReportPathsForProduct(product: Product): ReportPath[] {
    switch (product) {
      case Product.RE:
        return Object.values(ReadingEggsReportPath)
      case Product.REX:
        return Object.values(ReadingEggspressReportPath)
      case Product.MS:
        return Object.values(MathseedsReportPath)
      case Product.MX:
        return Object.values(MathleticsReportPath)
      case Product.FP:
        return Object.values(FastPhonicsReportPath)
      default:
        return []
    }
  }

  reportName(reportPath: ReportPath) {
    return this.intl.t(reportsRegistry[reportPath].labelKey)
  }

  reportDescription(reportPath: ReportPath) {
    return this.intl.t(reportsRegistry[reportPath].descriptionKey)
  }

  newReportsForSubscriptionType(subscriptionType: SubscriptionType) {
    return productsForSubscriptionType(subscriptionType)
      .flatMap((product) => this.reportPathsForProduct(product))
      .filter((reportPath) => hasNewReportForPath(reportPath))
  }

  newReportsForProduct(product: Product) {
    return this.reportPathsForProduct(product).filter((reportPath) => hasNewReportForPath(reportPath))
  }
}

export default ReportRegistryService

declare module '@ember/service' {
  interface Registry {
    'report-registry': ReportRegistryService
  }
}

export const isReportPath = (value?: Nullable<string>): value is ReportPath => {
  return typeof value === 'string' && value in reportsRegistry
}

export function hasNewReportForPath(reportPath: ReportPath) {
  return Boolean(reportsRegistry[reportPath].newReport)
}

/**
 * True if provided report uses the new standards system, requiring standards to be set
 */
export function isStandardsBasedReport(reportPath: ReportPath) {
  return Boolean(reportsRegistry[reportPath].requiresStandards)
}

export function isMultiSchoolReport(reportPath: ReportPath) {
  return Boolean(reportsRegistry[reportPath].multiSchoolSelection)
}

function isReportBlockedByStandardsSet(reportPath: ReportPath, standardsSet?: StandardsSet) {
  return isRexStandardsRestrictedReport(reportPath) && hasUnsupportedRexStandardsSet(standardsSet)
}

function isRexStandardsRestrictedReport(reportPath: ReportPath) {
  return (
    reportPath === ReadingEggspressReportPath.StandardsPerformance ||
    reportPath === ReadingEggspressReportPath.LessonPerformance
  )
}

function hasUnsupportedRexStandardsSet(standardsSet?: StandardsSet) {
  return Boolean(standardsSet && UNSUPPORTED_REX_STANDARDS_SET_NAMES.includes(standardsSet.name))
}

export enum ReadingEggsReportPath {
  StandardsPerformance = 'reporting.ui-scope.re.standards-performance',
  LessonPerformance = 'reporting.ui-scope.re.lesson-performance',
  Dashboard = 'reporting.ui-scope.re.dashboard.index',
  DashboardTable = 'reporting.ui-scope.re.dashboard.table',
  SummaryTable = 'reporting.ui-scope.re.summary.table',
  TimeOnTask = 'reporting.ui-scope.re.time-on-task',
  CourseProgressTotals = 'reporting.ui-scope.re.course-progress.totals',
  CourseProgressAverages = 'reporting.ui-scope.re.course-progress.averages',
  QuizzesCompleted = 'reporting.ui-scope.re.quizzes-completed',
  DrivingTests = 'reporting.ui-scope.re.driving-tests',
  LexileDetails = 'reporting.ui-scope.re.lexile-details',
  WorkingAtGradeLevel = 'reporting.ui-scope.re.working-at-grade-level',
  SpellingProgressTotals = 'reporting.ui-scope.re.spelling-progress.totals',
  SpellingProgressAverages = 'reporting.ui-scope.re.spelling-progress.averages',
  StoriesWritten = 'reporting.ui-scope.re.stories-written',
  Storylands = 'reporting.ui-scope.re.storylands',
  BooksRead = 'reporting.ui-scope.re.books-read',
  AwardsEarned = 'reporting.ui-scope.re.awards-earned',
}

export enum ReadingEggspressReportPath {
  StandardsPerformance = 'reporting.ui-scope.rex.standards-performance',
  LessonPerformance = 'reporting.ui-scope.rex.lesson-performance',
  Dashboard = 'reporting.ui-scope.rex.dashboard.index',
  DashboardTable = 'reporting.ui-scope.rex.dashboard.table',
  SummaryTable = 'reporting.ui-scope.rex.summary.table',
  TimeOnTask = 'reporting.ui-scope.rex.time-on-task',
  CourseProgressTotals = 'reporting.ui-scope.rex.course-progress.totals',
  CourseProgressAverages = 'reporting.ui-scope.rex.course-progress.averages',
  AssessmentScores = 'reporting.ui-scope.rex.assessment-scores',
  QuizScores = 'reporting.ui-scope.rex.quiz-scores',
  LexileDetails = 'reporting.ui-scope.rex.lexile-details',
  WorkingAtGradeLevel = 'reporting.ui-scope.rex.working-at-grade-level',
  SpellingProgressTotals = 'reporting.ui-scope.rex.spelling-progress.totals',
  SpellingProgressAverages = 'reporting.ui-scope.rex.spelling-progress.averages',
  BooksRead = 'reporting.ui-scope.rex.books-read.index',
  BooksReadByLevel = 'reporting.ui-scope.rex.books-read.by-level',
  AwardsEarned = 'reporting.ui-scope.rex.awards-earned',
}

export enum MathseedsReportPath {
  StandardsPerformance = 'reporting.ui-scope.maths.standards-performance',
  LessonPerformance = 'reporting.ui-scope.maths.lesson-performance',
  Dashboard = 'reporting.ui-scope.maths.dashboard.index',
  DashboardTable = 'reporting.ui-scope.maths.dashboard.table',
  SummaryTable = 'reporting.ui-scope.maths.summary.table',
  TimeOnTask = 'reporting.ui-scope.maths.time-on-task',
  CourseProgressTotals = 'reporting.ui-scope.maths.course-progress.totals',
  CourseProgressAverages = 'reporting.ui-scope.maths.course-progress.averages',
  DrivingTests = 'reporting.ui-scope.maths.driving-tests',
  QuizzesCompleted = 'reporting.ui-scope.maths.quizzes-completed',
  WorkingAtGradeLevel = 'reporting.ui-scope.maths.working-at-grade-level',
  AwardsEarned = 'reporting.ui-scope.maths.awards-earned',
  FluencySprints = 'reporting.ui-scope.maths.fluency-sprints.table',
}

export enum MathleticsReportPath {
  StandardsPerformance = 'reporting.ui-scope.mx.standards-performance',
}

export enum FastPhonicsReportPath {
  StandardsPerformance = 'reporting.ui-scope.fast-phonics.standards-performance',
  LessonPerformance = 'reporting.ui-scope.fast-phonics.lesson-performance',
}

export type ReportPath =
  | ReadingEggsReportPath
  | ReadingEggspressReportPath
  | MathseedsReportPath
  | MathleticsReportPath
  | FastPhonicsReportPath

type ReportRegistry = {
  [key in ReportPath]: {
    labelKey: string
    descriptionKey: string
    newReport?: boolean // reports that will show "new" notifications
    requiresStandards?: boolean // reports that use standards-based reporting system
    multiSchoolSelection?: boolean // reports that use new filters (eg multi-school selection, grades, content levels, larger default date range)
    featureFlag?: string // feature flag to enable/disabled the report
    featureAccess?: FeatureAccessId
  }
}

// a mapping of report paths to their translation keys (side nav label, page header description) and report config
const reportsRegistry: ReportRegistry = {
  // Product.RE
  [ReadingEggsReportPath.StandardsPerformance]: {
    labelKey: 'sideNav.reporting.re.standardsPerformance',
    descriptionKey: 'reporting.reportDescriptions.re.standardsPerformance',
    newReport: true,
    requiresStandards: true,
    multiSchoolSelection: true,
  },
  [ReadingEggsReportPath.LessonPerformance]: {
    labelKey: 'sideNav.reporting.lessonPerformanceIndividualStudents',
    descriptionKey: 'reporting.reportDescriptions.re.lessonPerformance',
    newReport: true,
    requiresStandards: true,
    multiSchoolSelection: true,
  },
  [ReadingEggsReportPath.Dashboard]: {
    labelKey: 'sideNav.reporting.re.dashboard',
    descriptionKey: 'reporting.reportDescriptions.re.dashboard',
  },
  [ReadingEggsReportPath.DashboardTable]: {
    labelKey: 'sideNav.reporting.re.dashboardTable',
    descriptionKey: 'reporting.reportDescriptions.re.dashboardTable',
  },
  [ReadingEggsReportPath.SummaryTable]: {
    labelKey: 'sideNav.reporting.re.summary',
    descriptionKey: 'reporting.reportDescriptions.re.summary',
  },
  [ReadingEggsReportPath.TimeOnTask]: {
    labelKey: 'sideNav.reporting.re.timeOnTask',
    descriptionKey: 'reporting.reportDescriptions.re.timeOnTask',
  },
  [ReadingEggsReportPath.CourseProgressTotals]: {
    labelKey: 'sideNav.reporting.re.courseProgressTotals',
    descriptionKey: 'reporting.reportDescriptions.re.courseProgressTotals',
  },
  [ReadingEggsReportPath.CourseProgressAverages]: {
    labelKey: 'sideNav.reporting.re.courseProgressAverages',
    descriptionKey: 'reporting.reportDescriptions.re.courseProgressAverages',
  },
  [ReadingEggsReportPath.QuizzesCompleted]: {
    labelKey: 'sideNav.reporting.re.quizzesCompleted',
    descriptionKey: 'reporting.reportDescriptions.re.quizzesCompleted',
  },
  [ReadingEggsReportPath.DrivingTests]: {
    labelKey: 'sideNav.reporting.re.drivingTests',
    descriptionKey: 'reporting.reportDescriptions.re.drivingTests',
  },
  [ReadingEggsReportPath.LexileDetails]: {
    labelKey: 'sideNav.reporting.re.lexileDetails',
    descriptionKey: 'reporting.reportDescriptions.re.lexileDetails',
  },
  [ReadingEggsReportPath.WorkingAtGradeLevel]: {
    labelKey: 'sideNav.reporting.re.workingAtGradeLevel',
    descriptionKey: 'reporting.reportDescriptions.re.workingAtGradeLevel',
  },
  [ReadingEggsReportPath.SpellingProgressTotals]: {
    labelKey: 'sideNav.reporting.re.spellingProgressTotals',
    descriptionKey: 'reporting.reportDescriptions.re.spellingProgressTotals',
  },
  [ReadingEggsReportPath.SpellingProgressAverages]: {
    labelKey: 'sideNav.reporting.re.spellingProgressAverages',
    descriptionKey: 'reporting.reportDescriptions.re.spellingProgressAverages',
  },
  [ReadingEggsReportPath.StoriesWritten]: {
    labelKey: 'sideNav.reporting.re.storiesWritten',
    descriptionKey: 'reporting.reportDescriptions.re.storiesWritten',
  },
  [ReadingEggsReportPath.Storylands]: {
    labelKey: 'sideNav.reporting.re.storylands',
    descriptionKey: 'reporting.reportDescriptions.re.storylands',
  },
  [ReadingEggsReportPath.BooksRead]: {
    labelKey: 'sideNav.reporting.re.booksRead',
    descriptionKey: 'reporting.reportDescriptions.re.booksRead',
  },
  [ReadingEggsReportPath.AwardsEarned]: {
    labelKey: 'sideNav.reporting.re.awardsEarned',
    descriptionKey: 'reporting.reportDescriptions.re.awardsEarned',
  },
  // Product.REX
  [ReadingEggspressReportPath.StandardsPerformance]: {
    labelKey: 'sideNav.reporting.re.standardsPerformance',
    descriptionKey: 'reporting.reportDescriptions.rex.standardsPerformance',
    newReport: true,
    requiresStandards: true,
    multiSchoolSelection: true,
    featureFlag: 'dui_rex_standards_and_lessons_reports',
  },
  [ReadingEggspressReportPath.LessonPerformance]: {
    labelKey: 'sideNav.reporting.lessonPerformanceIndividualStudents',
    descriptionKey: 'reporting.reportDescriptions.rex.lessonPerformance',
    newReport: true,
    requiresStandards: true,
    multiSchoolSelection: true,
    featureFlag: 'dui_rex_standards_and_lessons_reports',
  },
  [ReadingEggspressReportPath.Dashboard]: {
    labelKey: 'sideNav.reporting.rex.dashboard',
    descriptionKey: 'reporting.reportDescriptions.rex.dashboard',
  },
  [ReadingEggspressReportPath.DashboardTable]: {
    labelKey: 'sideNav.reporting.rex.dashboardTable',
    descriptionKey: 'reporting.reportDescriptions.rex.dashboardTable',
  },
  [ReadingEggspressReportPath.SummaryTable]: {
    labelKey: 'sideNav.reporting.rex.summary',
    descriptionKey: 'reporting.reportDescriptions.rex.summary',
  },
  [ReadingEggspressReportPath.TimeOnTask]: {
    labelKey: 'sideNav.reporting.rex.timeOnTask',
    descriptionKey: 'reporting.reportDescriptions.rex.timeOnTask',
  },
  [ReadingEggspressReportPath.CourseProgressTotals]: {
    labelKey: 'sideNav.reporting.rex.courseProgressTotals',
    descriptionKey: 'reporting.reportDescriptions.rex.courseProgressTotals',
  },
  [ReadingEggspressReportPath.CourseProgressAverages]: {
    labelKey: 'sideNav.reporting.rex.courseProgressAverages',
    descriptionKey: 'reporting.reportDescriptions.rex.courseProgressAverages',
  },
  [ReadingEggspressReportPath.AssessmentScores]: {
    labelKey: 'sideNav.reporting.rex.assessmentScores',
    descriptionKey: 'reporting.reportDescriptions.rex.assessmentScores',
  },
  [ReadingEggspressReportPath.QuizScores]: {
    labelKey: 'sideNav.reporting.rex.quizScores',
    descriptionKey: 'reporting.reportDescriptions.rex.quizScores',
  },
  [ReadingEggspressReportPath.LexileDetails]: {
    labelKey: 'sideNav.reporting.rex.lexileDetails',
    descriptionKey: 'reporting.reportDescriptions.rex.lexileDetails',
  },
  [ReadingEggspressReportPath.WorkingAtGradeLevel]: {
    labelKey: 'sideNav.reporting.rex.workingAtGradeLevel',
    descriptionKey: 'reporting.reportDescriptions.rex.workingAtGradeLevel',
  },
  [ReadingEggspressReportPath.SpellingProgressTotals]: {
    labelKey: 'sideNav.reporting.rex.spellingProgressTotals',
    descriptionKey: 'reporting.reportDescriptions.rex.spellingProgressTotals',
  },
  [ReadingEggspressReportPath.SpellingProgressAverages]: {
    labelKey: 'sideNav.reporting.rex.spellingProgressAverages',
    descriptionKey: 'reporting.reportDescriptions.rex.spellingProgressAverages',
  },
  [ReadingEggspressReportPath.BooksRead]: {
    labelKey: 'sideNav.reporting.rex.booksRead',
    descriptionKey: 'reporting.reportDescriptions.rex.booksRead',
  },
  [ReadingEggspressReportPath.BooksReadByLevel]: {
    labelKey: 'sideNav.reporting.rex.booksReadByLevel',
    descriptionKey: 'reporting.reportDescriptions.rex.booksReadByLevel',
  },
  [ReadingEggspressReportPath.AwardsEarned]: {
    labelKey: 'sideNav.reporting.rex.awardsEarned',
    descriptionKey: 'reporting.reportDescriptions.rex.awardsEarned',
  },
  // Product.FP
  [FastPhonicsReportPath.StandardsPerformance]: {
    labelKey: 'sideNav.reporting.fast-phonics.standardsPerformance',
    descriptionKey: 'reporting.reportDescriptions.fast-phonics.standardsPerformance',
    newReport: true,
    requiresStandards: true,
    multiSchoolSelection: true,
  },
  [FastPhonicsReportPath.LessonPerformance]: {
    labelKey: 'sideNav.reporting.lessonPerformanceIndividualStudents',
    descriptionKey: 'reporting.reportDescriptions.fast-phonics.lessonPerformance',
    newReport: true,
    requiresStandards: true,
    multiSchoolSelection: true,
  },
  // Product.MS
  [MathseedsReportPath.StandardsPerformance]: {
    labelKey: 'sideNav.reporting.maths.standardsPerformance',
    descriptionKey: 'reporting.reportDescriptions.maths.standardsPerformance',
    newReport: true,
    requiresStandards: true,
    multiSchoolSelection: true,
  },
  [MathseedsReportPath.LessonPerformance]: {
    labelKey: 'sideNav.reporting.lessonPerformanceIndividualStudents',
    descriptionKey: 'reporting.reportDescriptions.maths.lessonPerformance',
    newReport: true,
    requiresStandards: true,
    multiSchoolSelection: true,
  },
  [MathseedsReportPath.Dashboard]: {
    labelKey: 'sideNav.reporting.maths.dashboard',
    descriptionKey: 'reporting.reportDescriptions.maths.dashboard',
  },
  [MathseedsReportPath.DashboardTable]: {
    labelKey: 'sideNav.reporting.maths.dashboardTable',
    descriptionKey: 'reporting.reportDescriptions.maths.dashboardTable',
  },
  [MathseedsReportPath.SummaryTable]: {
    labelKey: 'sideNav.reporting.maths.summary',
    descriptionKey: 'reporting.reportDescriptions.maths.summary',
  },
  [MathseedsReportPath.TimeOnTask]: {
    labelKey: 'sideNav.reporting.maths.timeOnTask',
    descriptionKey: 'reporting.reportDescriptions.maths.timeOnTask',
  },
  [MathseedsReportPath.CourseProgressTotals]: {
    labelKey: 'sideNav.reporting.maths.courseProgressTotals',
    descriptionKey: 'reporting.reportDescriptions.maths.courseProgressTotals',
  },
  [MathseedsReportPath.CourseProgressAverages]: {
    labelKey: 'sideNav.reporting.maths.courseProgressAverages',
    descriptionKey: 'reporting.reportDescriptions.maths.courseProgressAverages',
  },
  [MathseedsReportPath.DrivingTests]: {
    labelKey: 'sideNav.reporting.maths.drivingTests',
    descriptionKey: 'reporting.reportDescriptions.maths.drivingTests',
  },
  [MathseedsReportPath.QuizzesCompleted]: {
    labelKey: 'sideNav.reporting.maths.quizzesCompleted',
    descriptionKey: 'reporting.reportDescriptions.maths.quizzesCompleted',
  },
  [MathseedsReportPath.WorkingAtGradeLevel]: {
    labelKey: 'sideNav.reporting.maths.workingAtGradeLevel',
    descriptionKey: 'reporting.reportDescriptions.maths.workingAtGradeLevel',
  },
  [MathseedsReportPath.AwardsEarned]: {
    labelKey: 'sideNav.reporting.maths.awardsEarned',
    descriptionKey: 'reporting.reportDescriptions.maths.awardsEarned',
  },
  [MathseedsReportPath.FluencySprints]: {
    labelKey: 'sideNav.reporting.maths.fluencySprints',
    descriptionKey: 'reporting.reportDescriptions.maths.fluencySprints',
  },
  // Product.MX
  [MathleticsReportPath.StandardsPerformance]: {
    labelKey: 'sideNav.reporting.mathletics.standardsPerformance',
    descriptionKey: 'reporting.reportDescriptions.mathletics.standardsPerformance',
    newReport: true,
    requiresStandards: true,
    multiSchoolSelection: true,
  },
  // Product.MSP
  // Product.WL
}
